import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, Check, Loader2, AlertCircle,
  User, Shirt, Package, Scissors, ShoppingCart,
  Minus, Plus, Layers, Shuffle, Trash2, ChevronDown, ChevronUp, MapPin, Zap, Clock, Truck, Store, X, Search,
} from 'lucide-react';
import axios from 'axios';
import { decorationPrice, placementSizeLabel, placementOptions, placementSupportsSize } from '../utils/quotePricing';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, label: 'Your Info', icon: User },
  { id: 2, label: 'Garment Source', icon: Shirt },
  { id: 3, label: 'Your Items', icon: Package },
  { id: 4, label: 'Review', icon: Check },
];

const COMPLEXITY_LABELS = {
  text_only: 'Text Only / Basic Logo',
  logo_with_words: 'Logo with Words',
  filled_logo_with_words: 'Filled Logo with Words',
};

const RUSH_OPTIONS = [
  { id: 'standard', label: 'Standard', fee: 0, days: '5 business days', desc: 'Default turnaround', icon: Clock },
  { id: 'expedited', label: 'Expedited Processing', fee: 50, days: '3 days', desc: '3-day production (delivery not guaranteed)', icon: Zap },
  { id: 'priority', label: 'Front of the Queue', fee: 100, days: '1 business day', desc: 'Your order jumps to the front, ASAP', icon: Zap },
];

const SHIPPING_OPTIONS = [
  { id: 'pickup', label: 'Pickup at KYIS Studio', fee: 0, desc: 'Pick up your order at our studio' },
  { id: 'standard', label: 'Standard Shipping', fee: 15, desc: 'Flat rate shipping' },
];

const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? 80 : -80, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -80 : 80, opacity: 0 }),
};

const EMPTY_ITEM = {
  source: 'catalog',
  product_code: null,
  product_name: '',
  product_type: null,
  decorationMethod: null,
  byo_description: '',
  quantity: 1,
  placements: [],
  sizeBreakdown: [],
  colorBreakdown: [],
  specialOptions: {},
  notes: '',
};

const normalizeSizeOptions = (options = {}) => (
  (options.sizes || []).map((s) => ({ code: s.code || s.label || s, label: s.label || s.code || s }))
);

const normalizeColorOptions = (options = {}) => (
  (options.colors || []).map((c) => ({ code: c.code, label: c.label || c.code, hex: c.hex }))
);

// colorBreakdown entries: [{code, sizes: {SIZE_CODE: qty}}] — per-unit pairs
const colorEntryQty = (e) => Object.values(e.sizes || {}).reduce((s, q) => s + (q || 0), 0);
const colorTotalQty = (breakdown = []) => breakdown.reduce((s, e) => s + colorEntryQty(e), 0);
const summarizeColorSizes = (breakdown = [], labelOf = (c) => c) => (
  breakdown
    .flatMap((e) => Object.entries(e.sizes || {})
      .filter(([, q]) => (q || 0) > 0)
      .map(([sz, q]) => `${labelOf(e.code)}${sz ? ` ${sz}` : ''} ×${q}`))
    .join(', ')
);

const summarizeSizeBreakdown = (breakdown = []) => (
  breakdown
    .filter((s) => (s.quantity || 0) > 0)
    .map((s) => `${s.code}: ${s.quantity}`)
    .join(', ')
);

/* ── Progress Bar ── */
const ProgressBar = ({ current }) => (
  <div className="flex items-center gap-1 w-full" data-testid="progress-bar">
    {STEPS.map((step) => {
      const done = current > step.id;
      const active = current === step.id;
      const Icon = step.icon;
      return (
        <div key={step.id} className="flex-1 flex flex-col items-center gap-1.5">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300
            ${done ? 'bg-[#34C759] text-white' : active ? 'bg-[#F5A623] text-[#0A1C29]' : 'bg-[#1E4159] text-[#A0B8C7]'}`}>
            {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
          </div>
          <span className={`text-[10px] font-medium hidden sm:block ${active ? 'text-[#F5A623]' : done ? 'text-[#34C759]' : 'text-[#A0B8C7]/60'}`}>
            {step.label}
          </span>
        </div>
      );
    })}
  </div>
);

/* ── Step 1: Customer Info ── */
const StepInfo = ({ data, onChange }) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Tell us about yourself</h2>
      <p className="text-[#A0B8C7] mt-1 text-sm" style={{ fontFamily: 'Manrope' }}>We'll use this to send your personalized quote.</p>
    </div>
    <div className="space-y-4">
      {[
        { key: 'name', label: 'Full Name', placeholder: 'Jane Smith', type: 'text', required: true },
        { key: 'email', label: 'Email Address', placeholder: 'jane@company.com', type: 'email', required: true },
        { key: 'phone', label: 'Phone Number', placeholder: '(555) 123-4567', type: 'tel' },
        { key: 'business', label: 'Business / Organization', placeholder: 'Acme Corp', type: 'text' },
      ].map(f => (
        <div key={f.key}>
          <label className="block text-sm font-medium text-[#A0B8C7] mb-1.5" style={{ fontFamily: 'Manrope' }}>
            {f.label} {f.required && <span className="text-[#F5A623]">*</span>}
          </label>
          <input type={f.type} value={data[f.key] || ''} onChange={e => onChange({ ...data, [f.key]: e.target.value })}
            placeholder={f.placeholder} data-testid={`input-${f.key}`}
            className="w-full px-4 py-3 rounded-xl bg-[#0A1C29] border border-[#1E4159] text-white placeholder-[#A0B8C7]/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/50 focus:border-[#F5A623] transition-all"
            style={{ fontFamily: 'Manrope' }} />
        </div>
      ))}
    </div>
  </div>
);

/* ── Step 2: Garment Source ── */
const StepSource = ({ value, onChange }) => {
  const options = [
    { id: 'shop', title: 'Use Our Garments', desc: 'Choose from our curated catalog of quality apparel', icon: Shirt },
    { id: 'byo', title: 'Bring Your Own', desc: "Send us your garments and we'll add your design", icon: Package },
    { id: 'mix', title: 'Mix & Match', desc: "Use some of ours and bring some of your own", icon: Shuffle },
  ];
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>How are you sourcing garments?</h2>
        <p className="text-[#A0B8C7] mt-1 text-sm" style={{ fontFamily: 'Manrope' }}>Pick the option that best fits your project.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {options.map(opt => {
          const active = value === opt.id;
          const Icon = opt.icon;
          return (
            <button key={opt.id} onClick={() => onChange(opt.id)} data-testid={`source-${opt.id}`}
              className={`relative p-6 rounded-2xl border-2 text-left transition-all duration-200 group
                ${active ? 'border-[#F5A623] bg-[#F5A623]/5 shadow-[0_0_24px_rgba(245,166,35,0.12)]'
                  : 'border-[#1E4159] bg-[#0F2A3D] hover:border-[#4A90A4]/50 hover:bg-[#163852]'}`}>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors
                ${active ? 'bg-[#F5A623] text-[#0A1C29]' : 'bg-[#1E4159] text-[#4A90A4] group-hover:text-[#F5A623]'}`}>
                <Icon className="w-6 h-6" />
              </div>
              <h3 className="font-bold text-white text-base mb-1" style={{ fontFamily: 'Outfit' }}>{opt.title}</h3>
              <p className="text-[#A0B8C7] text-xs leading-relaxed" style={{ fontFamily: 'Manrope' }}>{opt.desc}</p>
              {active && <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#F5A623] flex items-center justify-center"><Check className="w-3.5 h-3.5 text-[#0A1C29]" /></div>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ── Product Picker ── */
const ProductPicker = ({ products, selectedCode, onSelect }) => {
  const [open, setOpen] = useState(!selectedCode);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');

  const categories = useMemo(() => ['all', ...Array.from(new Set(products.map(p => p.category).filter(Boolean)))], [products]);
  const selectedProduct = products.find(p => p.code === selectedCode);
  const filteredProducts = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((p) => {
      const matchesCategory = category === 'all' || p.category === category;
      const matchesSearch = !q || [p.name, p.code, p.category, p.product_type].filter(Boolean).join(' ').toLowerCase().includes(q);
      return matchesCategory && matchesSearch;
    });
  }, [products, query, category]);

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="open-product-picker-btn"
        className={`w-full flex items-center justify-between px-4 py-4 rounded-xl border text-left transition-all
          ${selectedCode ? 'border-[#F5A623]/30 bg-[#F5A623]/5 hover:bg-[#F5A623]/10' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/50'}`}>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white truncate" style={{ fontFamily: 'Manrope' }}>
            {selectedProduct?.name || 'Choose a store garment'}
          </p>
          <p className="text-xs text-[#A0B8C7]/60 mt-0.5">
            {selectedProduct ? `${selectedProduct.category || 'Catalog'} · $${selectedProduct.msrp_low}-${selectedProduct.msrp_high} per unit` : 'Search or browse categories instead of scrolling a tiny list'}
          </p>
        </div>
        <span className="text-xs text-[#F5A623] font-bold whitespace-nowrap ml-3">{selectedProduct ? 'Change' : 'Browse'}</span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-[100] bg-[#06131D]/90 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-6"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <motion.div className="w-full max-w-5xl max-h-screen sm:max-h-[88vh] bg-[#0F2A3D] border border-[#1E4159] sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col"
              initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}>
              <div className="p-5 border-b border-[#1E4159] flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Choose store garments</h3>
                  <p className="text-sm text-[#A0B8C7] mt-1" style={{ fontFamily: 'Manrope' }}>Search the catalog, filter by category, then select the item to decorate.</p>
                </div>
                <button onClick={() => setOpen(false)} className="w-10 h-10 rounded-xl bg-[#0A1C29] border border-[#1E4159] text-[#A0B8C7] hover:text-white hover:border-[#4A90A4]/50 flex items-center justify-center" data-testid="close-product-picker-btn">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-5 border-b border-[#1E4159] space-y-4">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#4A90A4]" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by product name, code, or type..."
                    className="w-full pl-11 pr-4 py-3 rounded-xl bg-[#0A1C29] border border-[#1E4159] text-white placeholder-[#A0B8C7]/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40 focus:border-[#F5A623]"
                    style={{ fontFamily: 'Manrope' }} data-testid="product-search-input" />
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 custom-scrollbar">
                  {categories.map((cat) => (
                    <button key={cat} onClick={() => setCategory(cat)}
                      className={`px-3 py-2 rounded-lg border text-xs font-bold whitespace-nowrap transition-all
                        ${category === cat ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]' : 'border-[#1E4159] text-[#A0B8C7] hover:border-[#4A90A4]/50 hover:text-white'}`}
                      style={{ fontFamily: 'Outfit' }} data-testid={`product-category-${cat.replace(/\s+/g, '-').toLowerCase()}`}>
                      {cat === 'all' ? 'All Categories' : cat}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
                {filteredProducts.length === 0 ? (
                  <div className="py-16 text-center text-[#A0B8C7]" style={{ fontFamily: 'Manrope' }}>No garments matched your search.</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                    {filteredProducts.map((p) => {
                      const active = selectedCode === p.code;
                      return (
                        <button key={p.code} data-testid={`pick-${p.code}`} onClick={() => { onSelect(p); setOpen(false); }}
                          className={`p-4 rounded-2xl border-2 text-left transition-all min-h-[172px] flex gap-4
                            ${active ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/50 hover:bg-[#163852]'}`}>
                          <div className="w-20 h-24 rounded-xl bg-[#163852] border border-[#1E4159] overflow-hidden flex items-center justify-center flex-shrink-0">
                            {p.image_url ? (
                              <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              <Shirt className="w-8 h-8 text-[#4A90A4]/70" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0 flex flex-col justify-between">
                            <div>
                              <div className="flex items-start justify-between gap-3 mb-2">
                                <p className="text-sm font-bold text-white leading-snug" style={{ fontFamily: 'Manrope' }}>{p.name}</p>
                                {active && <Check className="w-4 h-4 text-[#F5A623] flex-shrink-0" />}
                              </div>
                              <p className="text-[10px] uppercase tracking-wider text-[#4A90A4] font-bold" style={{ fontFamily: 'Outfit' }}>{p.category || 'Catalog'}</p>
                            </div>
                            <div className="flex items-center justify-between mt-4">
                              <span className="text-[11px] text-[#A0B8C7]/60">{p.code}</span>
                              <span className="text-sm font-bold text-[#F5A623]" style={{ fontFamily: 'Outfit' }}>${p.msrp_low}-${p.msrp_high}</span>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

/* ── Product Type Picker (for BYO items) ── */
const ProductTypePicker = ({ sizeGuide, value, onChange }) => {
  const types = Object.entries(sizeGuide).map(([id, info]) => ({ id, label: info.label }));
  return (
    <div className="flex flex-wrap gap-2">
      {types.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} data-testid={`type-${t.id}`}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all
            ${value === t.id ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]' : 'border-[#1E4159] text-[#A0B8C7] hover:border-[#4A90A4]/40'}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
};

/* ── Size Breakdown Selector ── */
const SizeBreakdownSelector = ({ quantity, sizes, value, onChange }) => {
  const breakdown = value || [];
  const allocated = breakdown.reduce((sum, s) => sum + (s.quantity || 0), 0);
  const remaining = (quantity || 0) - allocated;
  const getQty = (code) => breakdown.find((s) => s.code === code)?.quantity || 0;
  const setQty = (code, qty) => {
    const next = breakdown.filter((s) => s.code !== code);
    if (qty > 0) next.push({ code, quantity: qty });
    onChange(next);
  };

  return (
    <div className="rounded-2xl border border-[#1E4159] bg-[#0A1C29] p-4" data-testid="size-breakdown-selector">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#4A90A4]" style={{ fontFamily: 'Outfit' }}>Sizes</label>
          <p className="text-[11px] text-[#A0B8C7]/60 mt-0.5" style={{ fontFamily: 'Manrope' }}>Assign the quantity across garment sizes.</p>
        </div>
        <span className={`text-xs font-bold ${remaining === 0 ? 'text-[#34C759]' : remaining < 0 ? 'text-red-400' : 'text-[#F5A623]'}`}>
          {allocated}/{quantity || 0}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {sizes.map((size) => {
          const qty = getQty(size.code);
          return (
            <div key={size.code} className={`rounded-xl border px-3 py-2 ${qty > 0 ? 'border-[#F5A623]/40 bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0F2A3D]'}`}>
              <div className="text-sm font-bold text-white mb-2" style={{ fontFamily: 'Outfit' }}>{size.label}</div>
              <div className="flex items-center justify-between gap-1">
                <button onClick={() => setQty(size.code, Math.max(0, qty - 1))} disabled={qty <= 0}
                  className="w-7 h-7 rounded-lg bg-[#1E4159] text-[#A0B8C7] hover:bg-red-500/20 hover:text-red-300 disabled:opacity-30 disabled:hover:bg-[#1E4159] disabled:hover:text-[#A0B8C7] flex items-center justify-center">
                  <Minus className="w-3 h-3" />
                </button>
                <span className="w-7 text-center text-sm font-bold text-white">{qty}</span>
                <button onClick={() => setQty(size.code, qty + 1)} disabled={remaining <= 0}
                  className="w-7 h-7 rounded-lg bg-[#1E4159] text-[#A0B8C7] hover:bg-[#F5A623] hover:text-[#0A1C29] disabled:opacity-30 disabled:hover:bg-[#1E4159] disabled:hover:text-[#A0B8C7] flex items-center justify-center">
                  <Plus className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {remaining !== 0 && (
        <p className="text-[11px] text-[#F5A623] mt-3" style={{ fontFamily: 'Manrope' }}>
          {remaining > 0 ? `${remaining} item${remaining !== 1 ? 's' : ''} still need a size.` : 'Too many sizes assigned — reduce one of the counts.'}
        </p>
      )}
    </div>
  );
};

const ColorBreakdownSelector = ({ quantity, colors, sizes, value, onChange }) => {
  // value: [{code, sizes: {SIZE_CODE: qty}}] — exact size+color pairs per unit
  const breakdown = value || [];
  const allocated = colorTotalQty(breakdown);
  const remaining = (quantity || 0) - allocated;
  const selectedCodes = new Set(breakdown.map((c) => c.code));
  const byCode = Object.fromEntries(colors.map((c) => [c.code, c]));
  const sizeList = (sizes && sizes.length > 0) ? sizes : [{ code: '', label: 'Qty' }];

  const toggle = (code) => {
    if (selectedCodes.has(code)) {
      onChange(breakdown.filter((c) => c.code !== code));
    } else {
      // Single size + single color (the common simple order): auto-fill it.
      const auto = (sizeList.length === 1 && breakdown.length === 0 && remaining > 0)
        ? { [sizeList[0].code]: remaining } : {};
      onChange([...breakdown, { code, sizes: auto }]);
    }
  };
  const setQty = (code, sizeCode, qty) => {
    onChange(breakdown.map((e) => (
      e.code === code ? { ...e, sizes: { ...e.sizes, [sizeCode]: Math.max(0, qty) } } : e
    )));
  };

  return (
    <div className="rounded-2xl border border-[#1E4159] bg-[#0A1C29] p-4" data-testid="color-breakdown-selector">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-[#4A90A4]" style={{ fontFamily: 'Outfit' }}>Colors &amp; Sizes</label>
          <p className="text-[11px] text-[#A0B8C7]/60 mt-0.5" style={{ fontFamily: 'Manrope' }}>Tap the colors you want, then set sizes within each color.</p>
        </div>
        <span className={`text-xs font-bold ${remaining === 0 && allocated > 0 ? 'text-[#34C759]' : remaining < 0 ? 'text-red-400' : 'text-[#F5A623]'}`}>
          {allocated}/{quantity || 0}
        </span>
      </div>

      {/* Compact swatch pills — tap to add/remove a color */}
      <div className="flex flex-wrap gap-1.5">
        {colors.map((color) => {
          const active = selectedCodes.has(color.code);
          return (
            <button key={color.code} type="button" onClick={() => toggle(color.code)} title={color.label}
              className={`flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 rounded-full border text-xs font-medium transition-all ${
                active
                  ? 'border-[#F5A623] bg-[#F5A623]/15 text-white'
                  : 'border-[#1E4159] bg-[#0F2A3D] text-[#A0B8C7] hover:border-[#4A90A4] hover:text-white'
              }`}
              data-testid={`color-pill-${color.code}`}>
              <span className="w-3.5 h-3.5 rounded-full border border-white/25 flex-shrink-0" style={{ backgroundColor: color.hex || '#888' }} />
              {color.label}
              {active && <Check className="w-3 h-3 text-[#F5A623]" />}
            </button>
          );
        })}
      </div>

      {/* Per-color size allocation */}
      {breakdown.map((entry) => {
        const c = byCode[entry.code] || { label: entry.code, hex: '#888' };
        return (
          <div key={entry.code} className="mt-3 rounded-xl border border-[#1E4159] bg-[#0F2A3D] p-3" data-testid={`color-sizes-${entry.code}`}>
            <div className="flex items-center gap-2 mb-2">
              <span className="w-4 h-4 rounded-full border border-white/25 flex-shrink-0" style={{ backgroundColor: c.hex || '#888' }} />
              <span className="text-sm font-bold text-white flex-1 truncate" style={{ fontFamily: 'Outfit' }}>{c.label}</span>
              <span className="text-xs text-[#A0B8C7]/70">{colorEntryQty(entry)} of {quantity || 0}</span>
              <button onClick={() => toggle(entry.code)} title="Remove color"
                className="w-6 h-6 rounded-lg text-[#A0B8C7]/50 hover:bg-red-500/20 hover:text-red-300 flex items-center justify-center flex-shrink-0">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {sizeList.map((sz) => {
                const qty = (entry.sizes || {})[sz.code] || 0;
                return (
                  <div key={sz.code || 'qty'} className={`flex items-center gap-1 rounded-lg border pl-2 pr-1 py-0.5 ${qty > 0 ? 'border-[#F5A623]/40 bg-[#F5A623]/5' : 'border-[#1E4159]'}`}>
                    <span className="text-xs font-bold text-white w-7">{sz.label}</span>
                    <button onClick={() => setQty(entry.code, sz.code, qty - 1)} disabled={qty <= 0}
                      className="w-6 h-6 rounded bg-[#1E4159] text-[#A0B8C7] hover:bg-red-500/20 hover:text-red-300 disabled:opacity-30 flex items-center justify-center">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-6 text-center text-xs font-bold text-white">{qty}</span>
                    <button onClick={() => setQty(entry.code, sz.code, qty + 1)} disabled={remaining <= 0}
                      className="w-6 h-6 rounded bg-[#1E4159] text-[#A0B8C7] hover:bg-[#F5A623] hover:text-[#0A1C29] disabled:opacity-30 flex items-center justify-center">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {breakdown.length === 0 && (
        <p className="text-[11px] text-[#A0B8C7]/50 mt-3" style={{ fontFamily: 'Manrope' }}>
          Tap a color above, then assign sizes inside it.
        </p>
      )}
      {breakdown.length > 0 && remaining !== 0 && (
        <p className="text-[11px] text-[#F5A623] mt-3" style={{ fontFamily: 'Manrope' }}>
          {remaining > 0 ? `${remaining} item${remaining !== 1 ? 's' : ''} still need a size & color.` : 'Too many assigned — reduce one of the counts.'}
        </p>
      )}
    </div>
  );
};

/* ── Placement Selector (location-aware) ── */
const PlacementSelector = ({ sizeGuide, productType, placements, onChange, pricingData, printingPricing, decorationMethod, specialOptions, onSpecialChange, onBatchUpdate }) => {
  const guide = sizeGuide[productType];
  const pricingModel = guide?.pricing_model || 'standard';
  const method = decorationMethod || 'embroidery';
  // Get locations from the correct method section
  const methodGuide = guide?.[method] || guide?.embroidery || {};
  const locations = methodGuide.locations || guide?.locations || [];

  if (pricingModel === 'standard' || !pricingModel) {
    return <StandardPlacementSelector locations={locations} placements={placements} onChange={onChange}
      pricingData={pricingData} printingPricing={printingPricing} decorationMethod={method} />;
  }
  if (pricingModel === 'fixed_price') {
    const fixedLocations = guide?.embroidery?.locations || [];
    return <LineJacketSelector guide={{...guide, locations: fixedLocations}} placements={placements} onChange={onChange} specialOptions={specialOptions} onBatchUpdate={onBatchUpdate} />;
  }
  if (pricingModel === 'stole') {
    return <StoleSelector guide={guide} placements={placements} onChange={onChange} specialOptions={specialOptions} onBatchUpdate={onBatchUpdate} />;
  }
  return null;
};

/* Standard embroidery placement selector */
const StandardPlacementSelector = ({ locations, placements, onChange, pricingData, printingPricing, decorationMethod }) => {
  const isPrinting = decorationMethod === 'printing';

  const toggleLocation = (loc) => {
    const existing = placements.find(p => p.location === loc.id);
    if (existing) {
      onChange(placements.filter(p => p.location !== loc.id));
    } else {
      const complexity = isPrinting ? undefined : loc.complexities?.[0];
      const option = placementOptions(loc, decorationMethod, complexity)[0];
      if (!option) return;
      const pl = { location: loc.id, label: loc.label, size: option.size,
        size_range: option.size_range, complexity, decoration_method: decorationMethod };
      onChange([...placements, pl]);
    }
  };

  const updatePlacement = (locId, field, value) => {
    const loc = locations.find(l => l.id === locId);
    onChange(placements.map(p => {
      if (p.location !== locId) return p;
      const next = { ...p, [field]: value };
      const options = placementOptions(loc, decorationMethod, next.complexity);
      const option = options.find(o => o.min <= next.size && next.size <= o.max) || options[0];
      return option ? { ...next, size: option.size, size_range: option.size_range } : next;
    }));
  };

  const getPrice = (placement) => {
    return decorationPrice(placement, decorationMethod, pricingData, printingPricing);
  };

  if (locations.length === 0) {
    return <p className="text-xs text-[#A0B8C7]/40 italic" style={{ fontFamily: 'Manrope' }}>No priced placements are available for this selection. Please choose another garment type or contact us for a custom quote.</p>;
  }

  return (
    <div className="space-y-3">
      {locations.map(loc => {
        const selected = placements.find(p => p.location === loc.id);
        const isActive = !!selected;
        const options = placementOptions(loc, decorationMethod, selected?.complexity || loc.complexities?.[0]);
        const selectedOption = options.find(o => o.min <= selected?.size && selected?.size <= o.max);
        return (
          <div key={loc.id} className={`rounded-xl border-2 overflow-hidden transition-all ${isActive ? 'border-[#F5A623]/40 bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29]'}`}>
            <button onClick={() => toggleLocation(loc)} data-testid={`loc-${loc.id}`}
              className="w-full flex items-center justify-between px-4 py-3 text-left">
              <div className="flex items-center gap-3">
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all flex-shrink-0
                  ${isActive ? 'border-[#F5A623] bg-[#F5A623]' : 'border-[#1E4159]'}`}>
                  {isActive && <Check className="w-3 h-3 text-[#0A1C29]" />}
                </div>
                <div>
                  <p className="text-sm font-medium text-white" style={{ fontFamily: 'Manrope' }}>{loc.label}</p>
                  <p className="text-[10px] text-[#A0B8C7]/50">
                    {selected ? `${placementSizeLabel(selected)}″ ${isPrinting ? 'print' : 'embroidery'}`
                      : isPrinting ? 'Print' : 'Embroidery'}
                  </p>
                </div>
              </div>
              {isActive && selected && (
                <span className="text-xs text-[#F5A623] font-semibold" data-testid={`loc-price-${loc.id}`}>
                  +${getPrice(selected).toFixed(2)}
                </span>
              )}
            </button>
            {isActive && (
              <div className="px-4 pb-4 space-y-3 border-t border-[#1E4159]/30 pt-3">
                {options.length > 1 && (
                  <div>
                    <label className="block text-[10px] font-medium text-[#A0B8C7]/60 mb-1.5" style={{ fontFamily: 'Manrope' }}>Size</label>
                    <div className="flex gap-2">
                      {options.map(option => (
                        <button key={option.size_range} onClick={() => updatePlacement(loc.id, 'size', option.size)} data-testid={`loc-${loc.id}-size-${option.size_range}`}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all
                            ${selectedOption === option ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]' : 'border-[#1E4159] text-[#A0B8C7] hover:border-[#4A90A4]/40'}`}>
                          {option.size_range}″
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {selectedOption?.placement_limit != null && (
                  <p className="text-xs text-[#A0B8C7]">This price range applies; maximum size at this placement is {selectedOption.placement_limit}″.</p>
                )}
                {!isPrinting && loc.complexities && (
                  <div>
                    <label className="block text-[10px] font-medium text-[#A0B8C7]/60 mb-1.5" style={{ fontFamily: 'Manrope' }}>Design Type</label>
                    <div className="flex flex-wrap gap-1.5">
                      {loc.complexities.map(c => (
                        <button key={c} onClick={() => updatePlacement(loc.id, 'complexity', c)} data-testid={`loc-${loc.id}-cx-${c}`}
                          className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium border transition-all
                            ${selected?.complexity === c ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]' : 'border-[#1E4159] text-[#A0B8C7] hover:border-[#4A90A4]/40'}`}>
                          {COMPLEXITY_LABELS[c]}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/* ── Line Jacket Selector (packages + à la carte) ── */
const LineJacketSelector = ({ guide, placements, onChange, specialOptions, onBatchUpdate }) => {
  const pkg = specialOptions?.package || null;
  const packages = guide.packages || [];
  const locations = guide.locations || [];

  const selectPackage = (pkgId) => {
    const pkgDef = packages.find(p => p.id === pkgId);
    const newPlacements = pkgDef
      ? pkgDef.included_locations.map(locId => {
          const loc = locations.find(l => l.id === locId);
          return { location: locId, label: loc?.label || locId, fixed_price: loc?.fixed_price || 0, included: true };
        })
      : [];
    onBatchUpdate({ placements: newPlacements, specialOptions: { ...specialOptions, package: pkgId } });
  };

  const toggleExtra = (loc) => {
    const existing = placements.find(p => p.location === loc.id);
    if (existing && !existing.included) {
      onChange(placements.filter(p => p.location !== loc.id));
    } else if (!existing) {
      onChange([...placements, { location: loc.id, label: loc.label, fixed_price: loc.fixed_price, included: false }]);
    }
  };

  const pkgDef = packages.find(p => p.id === pkg);
  const includedIds = pkgDef?.included_locations || [];
  const extraLocations = locations.filter(l => !includedIds.includes(l.id));

  return (
    <div className="space-y-4">
      {/* Package Selection */}
      <div>
        <label className="block text-xs font-medium text-[#A0B8C7] mb-2" style={{ fontFamily: 'Manrope' }}>Choose a Package</label>
        <div className="space-y-2">
          {packages.map(p => {
            const active = pkg === p.id;
            return (
              <button key={p.id} onClick={() => selectPackage(p.id)} data-testid={`pkg-${p.id}`}
                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 text-left transition-all
                  ${active ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/40'}`}>
                <div>
                  <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Manrope' }}>{p.label}</p>
                  <p className="text-xs text-[#A0B8C7]/60 mt-0.5">{p.description}</p>
                </div>
                <span className="text-sm font-bold text-[#F5A623] ml-4" style={{ fontFamily: 'Outfit' }}>${p.price}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Extra Placements (à la carte) */}
      {pkg && extraLocations.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-[#A0B8C7] mb-2" style={{ fontFamily: 'Manrope' }}>
            Additional Placements <span className="text-[#A0B8C7]/40">(optional)</span>
          </label>
          <div className="space-y-1.5">
            {extraLocations.map(loc => {
              const selected = placements.find(p => p.location === loc.id);
              const isActive = !!selected;
              return (
                <button key={loc.id} onClick={() => toggleExtra(loc)} data-testid={`extra-${loc.id}`}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border transition-all
                    ${isActive ? 'border-[#F5A623]/40 bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/30'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all
                      ${isActive ? 'border-[#F5A623] bg-[#F5A623]' : 'border-[#1E4159]'}`}>
                      {isActive && <Check className="w-2.5 h-2.5 text-[#0A1C29]" />}
                    </div>
                    <span className="text-sm text-white" style={{ fontFamily: 'Manrope' }}>{loc.label}</span>
                  </div>
                  <span className="text-xs text-[#A0B8C7] font-medium">+${loc.fixed_price}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Graduation Stole Selector ── */
const StoleSelector = ({ guide, placements, onChange, specialOptions, onBatchUpdate }) => {
  const baseOption = specialOptions?.stole_base || null;
  const hasLogo = specialOptions?.custom_logo || false;
  const baseOptions = guide.base_options || [];
  const locations = guide.locations || [];
  const additionalPrice = guide.additional_placement_price || 25;
  const logoFee = guide.logo_setup_fee || 50;

  const selectBase = (optId) => {
    let newPlacements;
    if (optId === 'one_side') {
      newPlacements = [{ location: 'right_side_middle', label: 'Right Side Middle', included: true }];
    } else {
      newPlacements = [
        { location: 'right_side_middle', label: 'Right Side Middle', included: true },
        { location: 'left_side_middle', label: 'Left Side Middle', included: true },
      ];
    }
    onBatchUpdate({ placements: newPlacements, specialOptions: { ...specialOptions, stole_base: optId } });
  };

  const toggleExtra = (loc) => {
    const existing = placements.find(p => p.location === loc.id);
    if (existing && !existing.included) {
      onChange(placements.filter(p => p.location !== loc.id));
    } else if (!existing) {
      onChange([...placements, { location: loc.id, label: loc.label, included: false }]);
    }
  };

  const includedIds = placements.filter(p => p.included).map(p => p.location);
  const availableExtra = locations.filter(l => !includedIds.includes(l.id));

  return (
    <div className="space-y-4">
      {/* Base Option */}
      <div>
        <label className="block text-xs font-medium text-[#A0B8C7] mb-2" style={{ fontFamily: 'Manrope' }}>Stole Package</label>
        <div className="space-y-2">
          {baseOptions.map(opt => {
            const active = baseOption === opt.id;
            return (
              <button key={opt.id} onClick={() => selectBase(opt.id)} data-testid={`stole-${opt.id}`}
                className={`w-full flex items-center justify-between p-4 rounded-xl border-2 text-left transition-all
                  ${active ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/40'}`}>
                <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Manrope' }}>{opt.label}</p>
                <span className="text-sm font-bold text-[#F5A623]" style={{ fontFamily: 'Outfit' }}>${opt.price}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Additional Placements */}
      {baseOption && availableExtra.length > 0 && (
        <div>
          <label className="block text-xs font-medium text-[#A0B8C7] mb-2" style={{ fontFamily: 'Manrope' }}>
            Additional Placements <span className="text-[#A0B8C7]/40">(+${additionalPrice} each)</span>
          </label>
          <div className="space-y-1.5">
            {availableExtra.map(loc => {
              const selected = placements.find(p => p.location === loc.id);
              const isActive = !!selected;
              return (
                <button key={loc.id} onClick={() => toggleExtra(loc)} data-testid={`stole-extra-${loc.id}`}
                  className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border transition-all
                    ${isActive ? 'border-[#F5A623]/40 bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/30'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all
                      ${isActive ? 'border-[#F5A623] bg-[#F5A623]' : 'border-[#1E4159]'}`}>
                      {isActive && <Check className="w-2.5 h-2.5 text-[#0A1C29]" />}
                    </div>
                    <span className="text-sm text-white" style={{ fontFamily: 'Manrope' }}>{loc.label}</span>
                  </div>
                  <span className="text-xs text-[#A0B8C7] font-medium">+${additionalPrice}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Custom Logo Option */}
      {baseOption && (
        <button onClick={() => onBatchUpdate({ specialOptions: { ...specialOptions, custom_logo: !hasLogo } })} data-testid="stole-logo-setup"
          className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all
            ${hasLogo ? 'border-[#F5A623]/40 bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/30'}`}>
          <div className="flex items-center gap-3">
            <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all
              ${hasLogo ? 'border-[#F5A623] bg-[#F5A623]' : 'border-[#1E4159]'}`}>
              {hasLogo && <Check className="w-2.5 h-2.5 text-[#0A1C29]" />}
            </div>
            <div>
              <span className="text-sm text-white" style={{ fontFamily: 'Manrope' }}>Custom Logo Setup</span>
              <p className="text-[10px] text-[#A0B8C7]/50">One-time fee for custom logo digitization</p>
            </div>
          </div>
          <span className="text-xs text-[#A0B8C7] font-medium">+${logoFee}</span>
        </button>
      )}
    </div>
  );
};

/* ── Single Item Card ── */
const ItemCard = ({ item, index, products, pricingData, printingPricing, sizeGuide, productOptions, garmentSource, onUpdate, onRemove, canRemove }) => {
  const [collapsed, setCollapsed] = useState(false);
  const showCatalog = item.source === 'catalog';
  const guide = sizeGuide[item.product_type];
  const pricingModel = guide?.pricing_model || 'standard';
  const decoMethods = guide?.decoration_methods || ['embroidery'];
  // Default to Embroidery whenever it is supported (most common path), otherwise use the first available method.
  const defaultMethod = decoMethods.includes('embroidery') ? 'embroidery' : decoMethods[0];
  const activeMethod = item.decorationMethod || defaultMethod;
  const selectedProductOptions = item.product_type ? (productOptions[item.product_type] || {}) : {};
  const sizeOptions = showCatalog && item.product_code ? normalizeSizeOptions(selectedProductOptions) : [];
  const colorOptions = showCatalog && item.product_code ? normalizeColorOptions(selectedProductOptions) : [];

  // Persist the auto-defaulted method onto the item so submission and
  // pricing logic don't have to re-derive it.
  useEffect(() => {
    if (item.product_type && !item.decorationMethod && defaultMethod) {
      onUpdate(index, { ...item, decorationMethod: defaultMethod });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.product_type]);

  const garmentPrice = useMemo(() => {
    if (pricingModel === 'fixed_price' || pricingModel === 'stole') return 0;
    if (!showCatalog || !item.product_code) return 0;
    const prod = products.find(p => p.code === item.product_code);
    return prod ? Math.round((prod.msrp_low + prod.msrp_high) / 2 * 100) / 100 : 0;
  }, [showCatalog, item.product_code, products, pricingModel]);

  const decoPrice = useMemo(() => {
    if (pricingModel === 'fixed_price') {
      const pkg = guide.packages?.find(p => p.id === item.specialOptions?.package);
      let total = pkg?.price || 0;
      for (const pl of item.placements) { if (!pl.included) total += pl.fixed_price || 0; }
      return total;
    }
    if (pricingModel === 'stole') {
      const baseOpt = guide.base_options?.find(o => o.id === item.specialOptions?.stole_base);
      let total = baseOpt?.price || 0;
      total += item.placements.filter(p => !p.included).length * (guide.additional_placement_price || 25);

      return total;
    }
    let total = 0;
    for (const pl of item.placements) {
      const method = pl.decoration_method || activeMethod || 'embroidery';
      total += decorationPrice(pl, method, pricingData, printingPricing);
    }
    return total;
  }, [item.placements, item.specialOptions, pricingData, printingPricing, pricingModel, guide, activeMethod]);

  const unitPrice = garmentPrice + decoPrice;
  const lineTotal = unitPrice * (item.quantity || 0) + (pricingModel === 'stole' && item.specialOptions?.custom_logo ? guide.logo_setup_fee || 50 : 0);
  const itemLabel = showCatalog
    ? (item.product_name || 'Select a product')
    : (item.byo_description || 'Describe your item');
  const typeLabel = item.product_type ? (sizeGuide[item.product_type]?.label || item.product_type) : '';

  const update = (field, val) => onUpdate(index, { ...item, [field]: val });

  return (
    <div className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] overflow-hidden" data-testid={`item-card-${index}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 cursor-pointer hover:bg-[#163852] transition-colors"
        onClick={() => setCollapsed(!collapsed)}>
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-7 h-7 rounded-full bg-[#F5A623]/15 flex items-center justify-center text-[#F5A623] font-bold text-xs flex-shrink-0" style={{ fontFamily: 'Outfit' }}>
            {index + 1}
          </span>
          <div className="min-w-0">
            <p className="text-sm font-medium text-white truncate" style={{ fontFamily: 'Manrope' }}>{itemLabel}</p>
            <div className="flex items-center gap-2">
              {typeLabel && <span className="text-[10px] text-[#4A90A4]">{typeLabel}</span>}
              {item.placements.length > 0 && <span className="text-[10px] text-[#A0B8C7]/40">{item.placements.length} placement{item.placements.length !== 1 ? 's' : ''}</span>}
              {item.quantity > 0 && unitPrice > 0 && (
                <span className="text-[10px] text-[#F5A623] font-medium">${lineTotal.toFixed(2)}</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canRemove && (
            <button onClick={e => { e.stopPropagation(); onRemove(index); }} data-testid={`remove-item-${index}`}
              className="p-1.5 rounded-lg text-[#A0B8C7]/40 hover:text-red-400 hover:bg-red-400/10 transition-all">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {collapsed ? <ChevronDown className="w-4 h-4 text-[#A0B8C7]/40" /> : <ChevronUp className="w-4 h-4 text-[#A0B8C7]/40" />}
        </div>
      </div>

      {!collapsed && (
        <div className="px-5 pb-5 space-y-5 border-t border-[#1E4159]/50">
          {/* Source toggle for Mix */}
          {garmentSource === 'mix' && (
            <div className="pt-4">
              <label className="block text-xs font-medium text-[#A0B8C7] mb-2" style={{ fontFamily: 'Manrope' }}>Item Source</label>
              <div className="flex gap-2">
                {[{ id: 'catalog', label: 'From Our Catalog' }, { id: 'byo', label: 'Bringing My Own' }].map(s => (
                  <button key={s.id} onClick={() => onUpdate(index, { ...item, source: s.id, product_code: null, product_name: '', product_type: null, placements: [] })}
                    data-testid={`item-${index}-source-${s.id}`}
                    className={`px-4 py-2 rounded-lg text-xs font-medium border transition-all
                      ${item.source === s.id ? 'border-[#F5A623] bg-[#F5A623]/10 text-[#F5A623]' : 'border-[#1E4159] text-[#A0B8C7] hover:border-[#4A90A4]/40'}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Product selection */}
          <div className={garmentSource !== 'mix' ? 'pt-4' : ''}>
            <label className="block text-xs font-medium text-[#A0B8C7] mb-2" style={{ fontFamily: 'Manrope' }}>
              {showCatalog ? 'Product' : 'Item Description'}
            </label>
            {showCatalog ? (
              <ProductPicker products={products} selectedCode={item.product_code}
                onSelect={p => onUpdate(index, { ...item, product_code: p.code, product_name: p.name, product_type: p.product_type, placements: [], sizeBreakdown: [], colorBreakdown: [] })} />
            ) : (
              <input type="text" value={item.byo_description}
                onChange={e => update('byo_description', e.target.value)}
                placeholder="e.g. Black baseball cap, Navy polo shirt..."
                data-testid={`byo-desc-${index}`}
                className="w-full px-4 py-3 rounded-xl bg-[#0A1C29] border border-[#1E4159] text-white placeholder-[#A0B8C7]/40 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/50 focus:border-[#F5A623] transition-all"
                style={{ fontFamily: 'Manrope' }} />
            )}
          </div>

          {/* Product type for BYO */}
          {!showCatalog && (
            <div>
              <label className="block text-xs font-medium text-[#A0B8C7] mb-2" style={{ fontFamily: 'Manrope' }}>What type of item is this?</label>
              <ProductTypePicker sizeGuide={sizeGuide} value={item.product_type}
                onChange={pt => onUpdate(index, { ...item, product_type: pt, placements: [] })} />
            </div>
          )}

          {/* Quantity */}
          <div>
            <label className="block text-xs font-medium text-[#A0B8C7] mb-2" style={{ fontFamily: 'Manrope' }}>Quantity</label>
            <div className="flex items-center gap-3">
              <button onClick={() => onUpdate(index, { ...item, quantity: Math.max(1, (item.quantity || 1) - 1), sizeBreakdown: [], colorBreakdown: [] })} data-testid={`qty-minus-${index}`}
                className="w-9 h-9 rounded-lg bg-[#1E4159] text-[#A0B8C7] hover:bg-[#F5A623] hover:text-[#0A1C29] flex items-center justify-center transition-all">
                <Minus className="w-3.5 h-3.5" />
              </button>
              <input type="number" min="1" value={item.quantity || ''} data-testid={`qty-input-${index}`}
                onChange={e => onUpdate(index, { ...item, quantity: Math.max(1, parseInt(e.target.value) || 1), sizeBreakdown: [], colorBreakdown: [] })}
                className="w-20 px-3 py-2 rounded-lg bg-[#0A1C29] border border-[#1E4159] text-white text-center text-sm font-bold focus:outline-none focus:ring-2 focus:ring-[#F5A623]/50 focus:border-[#F5A623]"
                style={{ fontFamily: 'Outfit' }} />
              <button onClick={() => onUpdate(index, { ...item, quantity: (item.quantity || 1) + 1, sizeBreakdown: [], colorBreakdown: [] })} data-testid={`qty-plus-${index}`}
                className="w-9 h-9 rounded-lg bg-[#1E4159] text-[#A0B8C7] hover:bg-[#F5A623] hover:text-[#0A1C29] flex items-center justify-center transition-all">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* With colors configured, sizes are assigned INSIDE each color so
              every unit has an exact size+color pair; the standalone size
              selector only appears for colorless product types. */}
          {showCatalog && item.product_code && colorOptions.length === 0 && sizeOptions.length > 0 && (
            <SizeBreakdownSelector
              quantity={item.quantity || 1}
              sizes={sizeOptions}
              value={item.sizeBreakdown || []}
              onChange={(sizeBreakdown) => update('sizeBreakdown', sizeBreakdown)}
            />
          )}
          {showCatalog && item.product_code && colorOptions.length > 0 && (
            <ColorBreakdownSelector
              quantity={item.quantity || 1}
              colors={colorOptions}
              sizes={sizeOptions}
              value={item.colorBreakdown || []}
              onChange={(colorBreakdown) => update('colorBreakdown', colorBreakdown)}
            />
          )}
          {showCatalog && item.product_code && sizeOptions.length === 0 && (
            <div className="rounded-2xl border border-[#F5A623]/30 bg-[#F5A623]/5 p-4" data-testid="missing-size-settings-warning">
              <p className="text-sm font-bold text-[#F5A623]" style={{ fontFamily: 'Outfit' }}>Sizes need to be configured</p>
              <p className="text-xs text-[#A0B8C7] mt-1" style={{ fontFamily: 'Manrope' }}>
                This product type does not have sizes configured in settings yet. Add sizes for {item.product_type} before customers can continue with this item.
              </p>
            </div>
          )}

          {/* Decoration: method selection + location-based */}
          {item.product_type && (
            <div className="pt-2 border-t border-[#1E4159]/50">
              {/* Decoration method selector (only if item supports both) */}
              {decoMethods.length > 1 && (
                <div className="mb-4">
                  <label className="block text-xs font-medium text-[#A0B8C7] mb-2" style={{ fontFamily: 'Manrope' }}>Decoration Type</label>
                  <div className="flex gap-2">
                    {decoMethods.map(m => {
                      const active = activeMethod === m;
                      return (
                        <button key={m} onClick={() => onUpdate(index, { ...item, decorationMethod: m, placements: [] })}
                          data-testid={`deco-method-${index}-${m}`}
                          className={`flex-1 px-4 py-3 rounded-xl border-2 text-center transition-all
                            ${active ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/40'}`}>
                          <p className="text-sm font-semibold text-white capitalize" style={{ fontFamily: 'Manrope' }}>{m}</p>
                          <p className="text-[10px] text-[#A0B8C7]/50 mt-0.5">
                            {m === 'embroidery' ? 'Stitched into the fabric' : 'Printed onto the fabric'}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Placement selector (shown once decoration method is determined) */}
              {activeMethod && (
                <>
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-[#4A90A4]" />
                    <label className="text-xs font-bold uppercase tracking-wider text-[#4A90A4]" style={{ fontFamily: 'Outfit' }}>
                      {pricingModel === 'fixed_price' ? 'Package & Placements' : pricingModel === 'stole' ? 'Stole Options' : `${activeMethod === 'printing' ? 'Print' : 'Embroidery'} Locations`} for {guide?.label || item.product_type}
                    </label>
                  </div>
                  <PlacementSelector
                    sizeGuide={sizeGuide} productType={item.product_type}
                    placements={item.placements} onChange={pl => update('placements', pl)}
                    pricingData={pricingData} printingPricing={printingPricing}
                    decorationMethod={activeMethod}
                    specialOptions={item.specialOptions}
                    onSpecialChange={opts => update('specialOptions', opts)}
                    onBatchUpdate={updates => onUpdate(index, { ...item, ...updates })}
                  />
                </>
              )}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs font-medium text-[#A0B8C7] mb-2" style={{ fontFamily: 'Manrope' }}>
              Notes <span className="text-[#A0B8C7]/40">(optional)</span>
            </label>
            <input type="text" value={item.notes || ''} onChange={e => update('notes', e.target.value)}
              placeholder="Color, special instructions..." data-testid={`item-notes-${index}`}
              className="w-full px-4 py-2.5 rounded-xl bg-[#0A1C29] border border-[#1E4159] text-white placeholder-[#A0B8C7]/40 text-xs focus:outline-none focus:ring-2 focus:ring-[#F5A623]/50 focus:border-[#F5A623] transition-all"
              style={{ fontFamily: 'Manrope' }} />
          </div>

          {/* Line total */}
          {unitPrice > 0 && item.quantity > 0 && (
            <div className="bg-[#0A1C29] rounded-xl p-3 flex items-center justify-between">
              <span className="text-xs text-[#A0B8C7]" style={{ fontFamily: 'Manrope' }}>
                {garmentPrice > 0 ? `$${garmentPrice.toFixed(2)} garment` : 'No garment cost'}
                {decoPrice > 0 ? ` + $${decoPrice.toFixed(2)} deco` : ''}
                {item.placements.length > 0 ? ` (${item.placements.length} placement${item.placements.length > 1 ? 's' : ''})` : ''}
                {' '}× {item.quantity}
              </span>
              <span className="text-sm font-bold text-[#F5A623]" style={{ fontFamily: 'Outfit' }} data-testid={`line-total-${index}`}>
                ${lineTotal.toFixed(2)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Step 3: Build Your Items ── */
const StepItems = ({ items, onItemsChange, products, pricingData, printingPricing, sizeGuide, productOptions, garmentSource }) => {
  const addItem = () => {
    const defaultSource = garmentSource === 'byo' ? 'byo' : 'catalog';
    onItemsChange(prev => [...prev, { ...EMPTY_ITEM, source: defaultSource }]);
  };
  const updateItem = (idx, updated) => onItemsChange(prev => prev.map((it, i) => i === idx ? updated : it));
  const removeItem = (idx) => onItemsChange(prev => prev.filter((_, i) => i !== idx));

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Build your items</h2>
        <p className="text-[#A0B8C7] mt-1 text-sm" style={{ fontFamily: 'Manrope' }}>
          Choose store garments, assign sizes, then pick decoration locations.
        </p>
      </div>
      <div className="space-y-4">
        {items.map((item, idx) => (
          <ItemCard key={idx} item={item} index={idx} products={products} pricingData={pricingData} printingPricing={printingPricing}
            sizeGuide={sizeGuide} productOptions={productOptions} garmentSource={garmentSource} onUpdate={updateItem} onRemove={removeItem} canRemove={items.length > 1} />
        ))}
      </div>
      <button onClick={addItem} data-testid="add-item-btn"
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border-2 border-dashed border-[#4A90A4]/30 text-[#4A90A4] font-semibold text-sm hover:border-[#4A90A4] hover:bg-[#4A90A4]/5 transition-all"
        style={{ fontFamily: 'Manrope' }}>
        <Plus className="w-4 h-4" /> Add Another Item
      </button>
    </div>
  );
};

/* ── Step 4: Review ── */
const StepReview = ({ customerInfo, garmentSource, items, sizeGuide, pricingData, printingPricing, products, rushOption, onRushChange, shippingOption, onShippingChange, byoAcknowledged, onByoAcknowledgedChange }) => {
  const sourceLabels = { shop: 'Using Our Garments', byo: 'Bringing Their Own', mix: 'Mix & Match' };
  const hasByo = garmentSource === 'byo' || items.some(i => i.source === 'byo');

  // Per-item price breakdown helper
  const getItemBreakdown = (item) => {
    const guide = sizeGuide[item.product_type];
    const model = guide?.pricing_model || 'standard';
    let garmentPrice = 0;
    if (model === 'standard' && item.source === 'catalog' && item.product_code) {
      const prod = products.find(p => p.code === item.product_code);
      if (prod) garmentPrice = Math.round((prod.msrp_low + prod.msrp_high) / 2 * 100) / 100;
    }
    let decoLines = [];
    let decoTotal = 0;
    const method = item.decorationMethod || (guide?.decoration_methods?.length === 1 ? guide.decoration_methods[0] : 'embroidery');

    if (model === 'fixed_price') {
      const pkg = guide.packages?.find(p => p.id === item.specialOptions?.package);
      if (pkg) { decoLines.push({ label: pkg.label, price: pkg.price }); decoTotal += pkg.price; }
      for (const pl of item.placements) {
        if (!pl.included && pl.fixed_price) { decoLines.push({ label: pl.label || pl.location, price: pl.fixed_price }); decoTotal += pl.fixed_price; }
      }
    } else if (model === 'stole') {
      const baseOpt = guide.base_options?.find(o => o.id === item.specialOptions?.stole_base);
      if (baseOpt) { decoLines.push({ label: baseOpt.label, price: baseOpt.price }); decoTotal += baseOpt.price; }
      const extras = item.placements.filter(p => !p.included);
      if (extras.length > 0) { const ep = extras.length * (guide.additional_placement_price || 25); decoLines.push({ label: `${extras.length} additional placement${extras.length > 1 ? 's' : ''}`, price: ep }); decoTotal += ep; }
      if (item.specialOptions?.custom_logo) decoLines.push({ label: 'Custom Logo Setup (once)', price: guide.logo_setup_fee || 50 });
    } else {
      for (const pl of item.placements) {
        const plMethod = pl.decoration_method || method;
        let price = 0;
        price = decorationPrice(pl, plMethod, pricingData, printingPricing);
        const desc = `${pl.label || pl.location}${pl.size ? ` ${placementSizeLabel(pl)}″` : ''}${pl.complexity ? ` ${COMPLEXITY_LABELS[pl.complexity]}` : ''} (${plMethod})`;
        decoLines.push({ label: desc, price });
        decoTotal += price;
      }
    }
    const unitPrice = garmentPrice + decoTotal;
    return { garmentPrice, decoLines, decoTotal, unitPrice, lineTotal: unitPrice * (item.quantity || 0) + (model === 'stole' && item.specialOptions?.custom_logo ? guide.logo_setup_fee || 50 : 0) };
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Outfit' }}>Review your quote</h2>
        <p className="text-[#A0B8C7] mt-1 text-sm" style={{ fontFamily: 'Manrope' }}>Make sure everything looks right before submitting.</p>
      </div>
      <div className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] p-5">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#4A90A4] mb-3" style={{ fontFamily: 'Outfit' }}>Contact</h3>
        <div className="grid grid-cols-2 gap-3 text-sm" style={{ fontFamily: 'Manrope' }}>
          <div><span className="text-[#A0B8C7]/60">Name</span><p className="text-white font-medium">{customerInfo.name}</p></div>
          <div><span className="text-[#A0B8C7]/60">Email</span><p className="text-white font-medium">{customerInfo.email}</p></div>
          {customerInfo.phone && <div><span className="text-[#A0B8C7]/60">Phone</span><p className="text-white font-medium">{customerInfo.phone}</p></div>}
          {customerInfo.business && <div><span className="text-[#A0B8C7]/60">Business</span><p className="text-white font-medium">{customerInfo.business}</p></div>}
        </div>
      </div>

      {/* Per-item cost breakdown */}
      <div className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] p-5">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#4A90A4] mb-4" style={{ fontFamily: 'Outfit' }}>Order Breakdown</h3>
        <div className="space-y-5">
          {items.map((item, idx) => {
            const label = item.source === 'catalog' ? (item.product_name || item.product_code) : (item.byo_description || 'Custom item');
            const typeLabel = item.product_type ? (sizeGuide[item.product_type]?.label || '') : '';
            const bd = getItemBreakdown(item);
            return (
              <div key={idx} className="border-b border-[#1E4159]/50 last:border-0 pb-4 last:pb-0" data-testid={`review-item-${idx}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Manrope' }}>{label}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {typeLabel && <span className="text-[10px] text-[#4A90A4]">{typeLabel}</span>}
                      {item.decorationMethod && <span className="text-[10px] text-[#A0B8C7]/50 capitalize">{item.decorationMethod}</span>}
                    </div>
                  </div>
                  <span className="text-sm font-bold text-[#F5A623]" style={{ fontFamily: 'Outfit' }}>${bd.lineTotal.toFixed(2)}</span>
                </div>
                <div className="ml-3 space-y-1 text-xs" style={{ fontFamily: 'Manrope' }}>
                  {bd.garmentPrice > 0 && (
                    <div className="flex justify-between text-[#A0B8C7]/70">
                      <span>Garment</span>
                      <span>${bd.garmentPrice.toFixed(2)}/ea</span>
                    </div>
                  )}
                  {item.source === 'catalog' && summarizeSizeBreakdown(item.sizeBreakdown).length > 0 && (
                    <div className="flex justify-between text-[#A0B8C7]/70">
                      <span>Sizes</span>
                      <span className="text-right">{summarizeSizeBreakdown(item.sizeBreakdown)}</span>
                    </div>
                  )}
                  {item.source === 'catalog' && summarizeColorSizes(item.colorBreakdown).length > 0 && (
                    <div className="flex justify-between text-[#A0B8C7]/70">
                      <span>Colors/Sizes</span>
                      <span className="text-right">{summarizeColorSizes(item.colorBreakdown)}</span>
                    </div>
                  )}
                  {bd.decoLines.map((dl, di) => (
                    <div key={di} className="flex justify-between text-[#A0B8C7]/70">
                      <span className="truncate mr-3">{dl.label}</span>
                      <span className="whitespace-nowrap">${dl.price.toFixed(2)}/ea</span>
                    </div>
                  ))}
                  <div className="flex justify-between text-[#A0B8C7] pt-1 border-t border-[#1E4159]/30 font-medium">
                    <span>${bd.unitPrice.toFixed(2)}/ea × {item.quantity}</span>
                    <span>${bd.lineTotal.toFixed(2)}</span>
                  </div>
                </div>
                {item.notes && <p className="text-[10px] text-[#A0B8C7]/40 mt-2 ml-3 italic">"{item.notes}"</p>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Processing Speed */}
      <div className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] p-5">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#4A90A4] mb-3" style={{ fontFamily: 'Outfit' }}>Processing Speed</h3>
        <div className="space-y-2">
          {RUSH_OPTIONS.map(opt => {
            const active = rushOption === opt.id;
            const Icon = opt.icon;
            return (
              <button key={opt.id} onClick={() => onRushChange(opt.id)} data-testid={`rush-${opt.id}`}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all
                  ${active ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/40'}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all
                  ${active ? 'bg-[#F5A623] text-[#0A1C29]' : 'bg-[#1E4159] text-[#A0B8C7]'}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Manrope' }}>{opt.label}</p>
                    {opt.fee > 0 ? (
                      <span className="text-sm font-bold text-[#F5A623]" style={{ fontFamily: 'Outfit' }}>+${opt.fee}</span>
                    ) : (
                      <span className="text-xs text-[#34C759] font-medium">Included</span>
                    )}
                  </div>
                  <p className="text-xs text-[#A0B8C7]/60 mt-0.5" style={{ fontFamily: 'Manrope' }}>{opt.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
        {rushOption !== 'standard' && hasByo && (
          <div className="mt-3 p-3 rounded-xl bg-[#F5A623]/5 border border-[#F5A623]/20" data-testid="byo-rush-disclaimer">
            <p className="text-xs text-[#F5A623] leading-relaxed" style={{ fontFamily: 'Manrope' }}>
              <span className="font-bold">Bring Your Own Items notice:</span> For items you are supplying, the processing timeline does not begin until your garments have been received at our studio. Please plan your delivery accordingly.
            </p>
          </div>
        )}
      </div>

      {/* Delivery Method */}
      <div className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] p-5">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#4A90A4] mb-3" style={{ fontFamily: 'Outfit' }}>Delivery Method</h3>
        <div className="space-y-2">
          {SHIPPING_OPTIONS.map(opt => {
            const active = shippingOption === opt.id;
            const Icon = opt.id === 'pickup' ? Store : Truck;
            return (
              <button key={opt.id} onClick={() => onShippingChange(opt.id)} data-testid={`shipping-${opt.id}`}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all
                  ${active ? 'border-[#F5A623] bg-[#F5A623]/5' : 'border-[#1E4159] bg-[#0A1C29] hover:border-[#4A90A4]/40'}`}>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-all
                  ${active ? 'bg-[#F5A623] text-[#0A1C29]' : 'bg-[#1E4159] text-[#A0B8C7]'}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold text-white" style={{ fontFamily: 'Manrope' }}>{opt.label}</p>
                    {opt.fee > 0 ? (
                      <span className="text-sm font-bold text-[#F5A623]" style={{ fontFamily: 'Outfit' }}>+${opt.fee}</span>
                    ) : (
                      <span className="text-xs text-[#34C759] font-medium">Free</span>
                    )}
                  </div>
                  <p className="text-xs text-[#A0B8C7]/60 mt-0.5" style={{ fontFamily: 'Manrope' }}>{opt.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* BYO Acknowledgment (required when any item is customer-supplied) */}
      {hasByo && (
        <div className="bg-[#F5A623]/5 rounded-2xl border-2 border-[#F5A623]/40 p-5" data-testid="byo-ack-block">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-[#F5A623] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-bold text-[#F5A623] mb-2" style={{ fontFamily: 'Outfit' }}>
                Bring Your Own Items: Timeline Acknowledgment
              </h3>
              <p className="text-xs text-[#A0B8C7] leading-relaxed mb-3" style={{ fontFamily: 'Manrope' }}>
                For items you are supplying, our processing timeline (standard, expedited, or priority) does
                not begin until your physical garments have been received at our studio. We cannot start work
                on your items until they are physically in-hand. Please plan your drop-off or shipping
                accordingly so we can meet your deadline.
              </p>
              <label className="flex items-start gap-2.5 cursor-pointer select-none group" data-testid="byo-ack-label">
                <input
                  type="checkbox"
                  checked={!!byoAcknowledged}
                  onChange={(e) => onByoAcknowledgedChange(e.target.checked)}
                  data-testid="byo-ack-checkbox"
                  className="mt-0.5 w-4 h-4 rounded border-2 border-[#1E4159] bg-[#0A1C29] text-[#F5A623] focus:ring-2 focus:ring-[#F5A623]/40 cursor-pointer accent-[#F5A623]"
                />
                <span className="text-xs text-white font-medium" style={{ fontFamily: 'Manrope' }}>
                  I understand that the production timeline starts only after my items arrive at KYIS, and
                  I will deliver them in time to meet my requested turnaround.
                </span>
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Sidebar ── */
const Sidebar = ({ customerInfo, garmentSource, items, pricingData, products, total, sizeGuide }) => {
  const sourceLabels = { shop: 'Our Garments', byo: 'Bring Your Own', mix: 'Mix & Match' };
  return (
    <div className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] overflow-hidden" data-testid="quote-sidebar">
      <div className="h-24 bg-gradient-to-br from-[#1E4159] to-[#0F2A3D] flex items-center justify-center">
        <div className="text-center">
          <Layers className="w-7 h-7 text-[#F5A623] mx-auto mb-1" />
          <p className="text-[10px] font-bold text-[#A0B8C7] uppercase tracking-wider" style={{ fontFamily: 'Outfit' }}>Quote Summary</p>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {customerInfo.name && (
          <div className="text-sm">
            <p className="text-white font-medium" style={{ fontFamily: 'Manrope' }}>{customerInfo.name}</p>
            {customerInfo.business && <p className="text-[#A0B8C7]/60 text-xs">{customerInfo.business}</p>}
          </div>
        )}
        {garmentSource && (
          <div className="flex items-center gap-2 text-xs text-[#A0B8C7]"><Shirt className="w-3.5 h-3.5 text-[#4A90A4]" />{sourceLabels[garmentSource]}</div>
        )}
        {items.filter(i => i.quantity > 0).length > 0 && (
          <div className="space-y-2 pt-2 border-t border-[#1E4159]">
            {items.filter(i => i.quantity > 0).map((item, idx) => {
              const label = item.source === 'catalog' ? (item.product_name || 'Product') : (item.byo_description || 'Custom item');
              return (
                <div key={idx} className="text-xs" style={{ fontFamily: 'Manrope' }}>
                  <div className="flex justify-between">
                    <span className="text-[#A0B8C7] truncate mr-2">{label}</span>
                    <span className="text-white font-medium whitespace-nowrap">×{item.quantity}</span>
                  </div>
                  {item.source === 'catalog' && summarizeSizeBreakdown(item.sizeBreakdown).length > 0 && (
                    <p className="text-[#A0B8C7]/40 text-[10px]">Sizes: {summarizeSizeBreakdown(item.sizeBreakdown)}</p>
                  )}
                  {item.source === 'catalog' && summarizeColorSizes(item.colorBreakdown).length > 0 && (
                    <p className="text-[#A0B8C7]/40 text-[10px]">Colors/Sizes: {summarizeColorSizes(item.colorBreakdown)}</p>
                  )}
                  {item.placements.length > 0 && (
                    <p className="text-[#A0B8C7]/40 text-[10px]">
                      {item.placements.map(p => `${p.label || p.location}${p.size ? ` ${placementSizeLabel(p)}″` : ''}`).join(', ')}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="pt-3 border-t border-[#1E4159] space-y-1">
          <div className="flex justify-between text-xs text-[#A0B8C7]"><span>Items</span><span>${total.itemsSubtotal.toFixed(2)}</span></div>
          {total.rushFee > 0 && (
            <div className="flex justify-between text-xs text-[#F5A623]"><span>Rush Fee</span><span>+${total.rushFee.toFixed(2)}</span></div>
          )}
          {total.shippingFee > 0 && (
            <div className="flex justify-between text-xs text-[#A0B8C7]"><span>Shipping</span><span>+${total.shippingFee.toFixed(2)}</span></div>
          )}
          <div className="flex justify-between text-xs text-[#A0B8C7]"><span>Tax (6%)</span><span>${total.tax.toFixed(2)}</span></div>
          <div className="flex justify-between text-sm font-bold text-white pt-1 border-t border-[#1E4159]" style={{ fontFamily: 'Outfit' }}>
            <span>Total</span><span data-testid="sidebar-total">${total.total.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-xs font-semibold text-[#F5A623]">
            <span>Deposit (50%)</span><span data-testid="sidebar-deposit">${total.deposit.toFixed(2)}</span>
          </div>
        </div>
        <p className="text-[10px] text-[#A0B8C7]/40" style={{ fontFamily: 'Manrope' }}>
          {total.rushFee > 0 ? (RUSH_OPTIONS.find(o => o.fee === total.rushFee)?.days || '5 business days') : '5 business days'} turnaround
          {total.shippingFee > 0 ? ' · Shipped to you' : ' · Pickup at KYIS Studio'}
        </p>
      </div>
    </div>
  );
};

/* ══════════════════════════════════
   Main Quote Builder: 4-Step Wizard
   ══════════════════════════════════ */
const QuoteBuilderPage = ({ inStoreMode = false } = {}) => {
  const [searchParams] = useSearchParams();
  const editQuoteId = searchParams.get('edit');
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [products, setProducts] = useState([]);
  const [pricingData, setPricingData] = useState({});
  const [printingPricing, setPrintingPricing] = useState({});
  const [sizeGuide, setSizeGuide] = useState({});
  const [productOptions, setProductOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [quoteResult, setQuoteResult] = useState(null);
  const [editingQuoteId, setEditingQuoteId] = useState(editQuoteId);
  const [skipSourceReset, setSkipSourceReset] = useState(false);

  const [customerInfo, setCustomerInfo] = useState({ name: '', email: '', phone: '', business: '' });
  const [garmentSource, setGarmentSource] = useState('');
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [rushOption, setRushOption] = useState('standard');
  const [shippingOption, setShippingOption] = useState('pickup');
  const [byoAcknowledged, setByoAcknowledged] = useState(false);

  const hasByoItems = useMemo(
    () => garmentSource === 'byo' || items.some(i => i.source === 'byo'),
    [garmentSource, items],
  );

  useEffect(() => { loadCatalog(); }, []);

  const loadCatalog = async () => {
    try {
      const [prodRes, priceRes, guideRes] = await Promise.all([
        axios.get(`${API}/api/quotes/products`),
        axios.get(`${API}/api/quotes/pricing`),
        axios.get(`${API}/api/quotes/size-guide`),
      ]);
      const loadedProducts = prodRes.data.products || [];
      setProducts(loadedProducts);
      setPricingData({ ...priceRes.data.pricing, ranges: priceRes.data.embroidery_ranges || [] });
      setPrintingPricing(priceRes.data.printing_pricing || {});
      setSizeGuide(guideRes.data.guide);

      const uniqueTypes = Array.from(new Set(loadedProducts.map(p => p.product_type).filter(Boolean)));
      const optionPairs = await Promise.all(uniqueTypes.map(async (pt) => {
        try {
          const optRes = await axios.get(`${API}/api/order-config/options/${pt}`);
          return [pt, optRes.data];
        } catch {
          return [pt, { sizes: [], colors: [] }];
        }
      }));
      setProductOptions(Object.fromEntries(optionPairs));

      // If editing an existing quote, load it after catalog is ready
      if (editQuoteId) {
        await loadExistingQuote(editQuoteId, loadedProducts);
      }
    } catch { setError('Failed to load catalog'); }
    finally { setLoading(false); }
  };

  const loadExistingQuote = async (quoteId, productsList) => {
    try {
      const res = await axios.get(`${API}/api/quotes/${quoteId}`);
      const q = res.data;

      setCustomerInfo({
        name: q.customer_name || '',
        email: q.customer_email || '',
        phone: q.customer_phone || '',
        business: q.business_name || '',
      });

      if (q.rush_option) setRushOption(q.rush_option);
      if (q.shipping_option) setShippingOption(q.shipping_option);

      // Determine garment source from items
      const sources = (q.items || []).map(i => i.garment_source);
      const hasByo = sources.includes('byo');
      const hasShop = sources.includes('shop');
      const source = hasByo && hasShop ? 'mix' : hasByo ? 'byo' : 'shop';
      setSkipSourceReset(true);
      setGarmentSource(source);

      // Rebuild items from quote items
      const rebuiltItems = (q.items || []).map(qi => {
        const product = productsList.find(p => p.code === qi.product_code);
        const isByo = qi.garment_source === 'byo';
        let placements = [];
        let specialOptions = {};
        if (qi.placements_json) {
          const pj = typeof qi.placements_json === 'string' ? JSON.parse(qi.placements_json) : qi.placements_json;
          specialOptions = pj?.[0]?.special_options || {};
          placements = (pj || []).map(p => ({
            location: p.location,
            label: p.label || p.location,
            size: p.size,
            size_range: p.size_range,
            decoration_method: p.decoration_method,
            complexity: p.complexity,
            fixed_price: p.fixed_price || p.price,
            included: p.included,
          }));
        }
        return {
          source: isByo ? 'byo' : 'catalog',
          product_code: qi.product_code,
          product_name: qi.product_name || product?.name || '',
          product_type: product?.product_type || null,
          byo_description: isByo ? (qi.product_name || '') : '',
          quantity: qi.quantity || 1,
          placements,
          specialOptions,
          notes: qi.notes || '',
        };
      });

      if (rebuiltItems.length > 0) {
        setItems(rebuiltItems);
      }

      // Jump to step 3 so they can review/edit items directly
      setStep(3);
    } catch (e) {
      setError('Failed to load quote for editing');
    }
  };

  useEffect(() => {
    if (!garmentSource) return;
    if (skipSourceReset) { setSkipSourceReset(false); return; }
    const src = garmentSource === 'byo' ? 'byo' : 'catalog';
    setItems([{ ...EMPTY_ITEM, source: src }]);
  }, [garmentSource]);

  const rushFee = useMemo(() => {
    const opt = RUSH_OPTIONS.find(o => o.id === rushOption);
    return opt ? opt.fee : 0;
  }, [rushOption]);

  const shippingFee = useMemo(() => {
    const opt = SHIPPING_OPTIONS.find(o => o.id === shippingOption);
    return opt ? opt.fee : 0;
  }, [shippingOption]);

  const total = useMemo(() => {
    let itemsSubtotal = 0;
    items.forEach(item => {
      const guide = sizeGuide[item.product_type];
      const model = guide?.pricing_model || 'standard';

      let gp = 0;
      if (model === 'standard' && item.source === 'catalog' && item.product_code) {
        const prod = products.find(p => p.code === item.product_code);
        if (prod) gp = Math.round((prod.msrp_low + prod.msrp_high) / 2 * 100) / 100;
      }

      let dp = 0;
      if (model === 'fixed_price') {
        const pkg = guide.packages?.find(p => p.id === item.specialOptions?.package);
        dp = pkg?.price || 0;
        for (const pl of item.placements) {
          if (!pl.included) dp += pl.fixed_price || 0;
        }
      } else if (model === 'stole') {
        const baseOpt = guide.base_options?.find(o => o.id === item.specialOptions?.stole_base);
        dp = baseOpt?.price || 0;
        dp += item.placements.filter(p => !p.included).length * (guide.additional_placement_price || 25);

      } else {
        const method = item.decorationMethod || (guide?.decoration_methods?.length === 1 ? guide.decoration_methods[0] : 'embroidery');
        for (const pl of item.placements) {
          const plMethod = pl.decoration_method || method;
          dp += decorationPrice(pl, plMethod, pricingData, printingPricing);
        }
      }
      itemsSubtotal += (gp + dp) * (item.quantity || 0) + (model === 'stole' && item.specialOptions?.custom_logo ? guide.logo_setup_fee || 50 : 0);
    });
    const subtotal = itemsSubtotal + rushFee + shippingFee;
    const tax = Math.round(subtotal * 0.06 * 100) / 100;
    return { itemsSubtotal, rushFee, shippingFee, subtotal, tax, total: Math.round((subtotal + tax) * 100) / 100, deposit: Math.round((subtotal + tax) * 0.5 * 100) / 100 };
  }, [items, products, pricingData, printingPricing, sizeGuide, rushFee, shippingFee]);

  const canProceed = useMemo(() => {
    switch (step) {
      case 1: return customerInfo.name.trim() && customerInfo.email.trim() && customerInfo.email.includes('@');
      case 2: return !!garmentSource;
      case 3: return items.length > 0 && items.every(it => {
        if (it.quantity <= 0) return false;
        if (it.source === 'catalog' ? !it.product_code : !it.byo_description.trim()) return false;
        if (!it.product_type) return false;
        if (it.source === 'catalog') {
          if ((productOptions[it.product_type]?.colors || []).length > 0) {
            // Colors configured: every unit needs an exact size+color pair
            if (colorTotalQty(it.colorBreakdown || []) !== it.quantity) return false;
          } else {
            const assignedSizes = (it.sizeBreakdown || []).reduce((sum, s) => sum + (s.quantity || 0), 0);
            if (assignedSizes !== it.quantity) return false;
          }
        }
        const g = sizeGuide[it.product_type];
        const m = g?.pricing_model || 'standard';
        if (m === 'fixed_price') return !!it.specialOptions?.package;
        if (m === 'stole') return !!it.specialOptions?.stole_base;
        return it.placements.length > 0 && it.placements.every(pl => {
          const method = pl.decoration_method || it.decorationMethod || 'embroidery';
          const location = g?.[method]?.locations?.find(loc => loc.id === pl.location);
          const price = decorationPrice(pl, method, pricingData, printingPricing);
          return placementSupportsSize(location, method, pl.complexity, pl.size)
            && (method !== 'embroidery' || location.complexities?.includes(pl.complexity))
            && Number.isFinite(Number(price)) && Number(price) > 0;
        });
      });
      case 4: return !hasByoItems || byoAcknowledged;
      default: return false;
    }
  }, [step, customerInfo, garmentSource, items, sizeGuide, productOptions, hasByoItems, byoAcknowledged, pricingData, printingPricing]);

  const goNext = () => { setDirection(1); setStep(s => Math.min(s + 1, 4)); };
  const goBack = () => { setDirection(-1); setStep(s => Math.max(s - 1, 1)); };

  // Progressive lead save: fire once per session when the customer completes
  // Step 1. Upserts a contact into HighLevel with an "abandoned-quote-lead"
  // tag so we still have a contactable lead if they bail out mid-wizard.
  const progressiveLeadSentForRef = useRef(null);
  const handleContinueFromStep1 = useCallback(async () => {
    const emailKey = (customerInfo.email || '').trim().toLowerCase();
    if (
      !inStoreMode &&
      !editingQuoteId &&
      emailKey &&
      progressiveLeadSentForRef.current !== emailKey
    ) {
      progressiveLeadSentForRef.current = emailKey;
      try {
        await axios.post(`${API}/api/quotes/progressive-lead`, {
          name: customerInfo.name,
          email: customerInfo.email,
          phone: customerInfo.phone || null,
          business: customerInfo.business || null,
        });
      } catch (e) {
        // Fire and forget: never block the wizard on CRM sync.
        // eslint-disable-next-line no-console
        console.warn('Progressive lead save failed (non-blocking):', e?.response?.data || e?.message);
      }
    }
    goNext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerInfo, inStoreMode, editingQuoteId]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const backendItems = items.filter(i => i.quantity > 0).map(item => {
        const guide = sizeGuide[item.product_type];
        const decoMethods = guide?.decoration_methods || ['embroidery'];
        const itemMethod = item.decorationMethod
          || (decoMethods.includes('embroidery') ? 'embroidery' : decoMethods[0]);
        return {
          garment_source: item.source === 'catalog' ? 'shop' : 'byo',
          product_code: item.source === 'catalog' ? item.product_code : null,
          product_name: item.source === 'catalog' ? item.product_name : item.byo_description,
          product_type: item.product_type,
          special_options: item.specialOptions || {},
          decoration_method: itemMethod,
          placements: item.placements.map(pl => ({
            location: pl.location,
            size: pl.size,
            complexity: pl.complexity,
            decoration_method: pl.decoration_method || itemMethod,
          })),
          quantity: item.quantity,
          // Exact per-unit pairs [{size, color, quantity}] plus derived totals.
          // The notes string below is a human-readable copy only.
          ...(() => {
            const isCatalog = item.source === 'catalog';
            const pairs = isCatalog
              ? (item.colorBreakdown || []).flatMap(e =>
                  Object.entries(e.sizes || {})
                    .filter(([, q]) => (q || 0) > 0)
                    .map(([sz, q]) => ({ color: e.code, size: sz || null, quantity: q })))
              : [];
            const agg = (key) => {
              const m = {};
              pairs.forEach(p => { if (p[key]) m[p[key]] = (m[p[key]] || 0) + p.quantity; });
              return Object.keys(m).length ? m : null;
            };
            const plainSizes = isCatalog && (item.sizeBreakdown || []).some(s => (s.quantity || 0) > 0)
              ? Object.fromEntries((item.sizeBreakdown || []).filter(s => (s.quantity || 0) > 0).map(s => [s.code, s.quantity]))
              : null;
            return {
              size_color_breakdown: pairs.length ? pairs : null,
              size_breakdown: pairs.length ? agg('size') : plainSizes,
              color_breakdown: pairs.length ? agg('color') : null,
            };
          })(),
          notes: [
            item.source === 'catalog' && summarizeColorSizes(item.colorBreakdown) ? `Units: ${summarizeColorSizes(item.colorBreakdown)}` : '',
            item.source === 'catalog' && !summarizeColorSizes(item.colorBreakdown) && summarizeSizeBreakdown(item.sizeBreakdown) ? `Sizes: ${summarizeSizeBreakdown(item.sizeBreakdown)}` : '',
            item.notes,
          ].filter(Boolean).join(' | '),
        };
      });
      const res = await axios.post(`${API}/api/quotes/create`, {
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone,
        business_name: customerInfo.business,
        notes: items.map(i => i.notes).filter(Boolean).join(' | '),
        rush_option: rushOption,
        shipping_option: shippingOption,
        items: backendItems,
      });
      setQuoteResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to generate quote');
    } finally { setSubmitting(false); }
  };

  const handlePayDeposit = async () => {
    if (!quoteResult) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/quotes/${quoteResult.quote_id}/checkout`, { origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (e) { setError(e.response?.data?.detail || 'Checkout failed'); setSubmitting(false); }
  };

  // In-store mode: skip Stripe, run the same conversion flow on the backend
  const [inStoreTender, setInStoreTender] = useState('Cash');
  const [inStoreResult, setInStoreResult] = useState(null);
  const handleMarkPaidInStore = async () => {
    if (!quoteResult) return;
    setSubmitting(true);
    setError('');
    try {
      const token = localStorage.getItem('kyis_token');
      const res = await axios.post(
        `${API}/api/quotes/admin/${quoteResult.quote_id}/mark-paid-in-store`,
        { tender: inStoreTender },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setInStoreResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to mark paid');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A1C29] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#F5A623] animate-spin" /></div>;
  }

  if (quoteResult) {
    // In-store success view
    if (inStoreMode && inStoreResult) {
      return (
        <div className="min-h-screen bg-[#0A1C29] flex items-center justify-center px-4" data-testid="instore-paid-success">
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] p-8 max-w-md w-full text-center">
            <div className="w-16 h-16 rounded-full bg-[#34C759]/10 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-[#34C759]" /></div>
            <h2 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit' }}>Marked Paid In-Store</h2>
            <p className="text-[#A0B8C7] text-sm mb-6">Quote <span className="text-[#F5A623] font-semibold">#{inStoreResult.quote_number}</span> → Order <span className="text-[#F5A623] font-semibold">#{inStoreResult.order_number}</span></p>
            <div className="bg-[#0A1C29] rounded-xl p-4 mb-6 space-y-2 text-left text-sm">
              <div className="flex justify-between"><span className="text-[#A0B8C7]">Tender</span><span className="text-white">{inStoreResult.tender || 'In-Store'}</span></div>
              <div className="flex flex-wrap justify-between gap-2"><span className="text-[#A0B8C7]">Customer</span><span className="text-white break-words">{inStoreResult.portal_customer?.email}</span></div>
              {inStoreResult.setup_email_queued && (
                <p className="text-sm text-[#A0B8C7]">A setup link will be emailed so the customer can choose their password.</p>
              )}
            </div>
            <p className="text-sm text-[#A0B8C7] mb-4">Order created. Receipt delivery and Prometheus sync are queued.</p>
            <Link to="/admin/orders" className="block w-full py-3 rounded-xl font-bold text-sm bg-[#F5A623] text-[#0A1C29] hover:bg-[#D88F1B] transition mb-2">View Order</Link>
            <Link to="/admin/in-store-quote" onClick={() => window.location.reload()} className="block text-sm text-[#A0B8C7] hover:text-white transition">+ Start Another Quote</Link>
          </motion.div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#0A1C29] flex items-center justify-center px-4" data-testid="quote-success">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
          className="bg-[#0F2A3D] rounded-2xl border border-[#1E4159] p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-full bg-[#34C759]/10 flex items-center justify-center mx-auto mb-4"><Check className="w-8 h-8 text-[#34C759]" /></div>
          <h2 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Outfit' }}>Quote Generated</h2>
          <p className="text-[#A0B8C7] text-sm mb-6" style={{ fontFamily: 'Manrope' }}>Quote <span className="text-[#F5A623] font-semibold">#{quoteResult.quote_number}</span></p>
          <div className="bg-[#0A1C29] rounded-xl p-4 mb-6 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-[#A0B8C7]">Total</span><span className="text-white font-bold">${quoteResult.total.toFixed(2)}</span></div>
            <div className="flex justify-between text-sm"><span className="text-[#F5A623]">Deposit (50%)</span><span className="text-[#F5A623] font-bold">${quoteResult.deposit_amount.toFixed(2)}</span></div>
          </div>
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-2 rounded-xl mb-4 text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {error}</div>}
          {inStoreMode ? (
            <>
              <div className="text-left mb-3">
                <label className="text-xs text-[#A0B8C7] uppercase tracking-wider font-semibold mb-1.5 block">Tender (receipt label)</label>
                <select value={inStoreTender} onChange={(e) => setInStoreTender(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-lg bg-[#0A1C29] border border-[#1E4159] text-white text-sm"
                  data-testid="instore-tender-select">
                  <option>Cash</option>
                  <option>Card (Shift4 / HarborTouch)</option>
                  <option>Check</option>
                  <option>Other</option>
                </select>
              </div>
              <button onClick={handleMarkPaidInStore} disabled={submitting} data-testid="mark-paid-instore-btn"
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm bg-[#F5A623] text-[#0A1C29] hover:bg-[#D88F1B] disabled:opacity-40 transition-all" style={{ fontFamily: 'Outfit' }}>
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Processing...</> : <><Check className="w-4 h-4" /> Mark Paid &amp; Convert to Order</>}
              </button>
              <p className="text-[10px] text-[#A0B8C7]/60 mt-3">Skips Stripe. Runs full conversion: order, portal account, welcome email, Prometheus sync.</p>
            </>
          ) : (
            <button onClick={handlePayDeposit} disabled={submitting} data-testid="pay-deposit-btn"
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold text-sm bg-[#F5A623] text-[#0A1C29] hover:bg-[#D88F1B] disabled:opacity-40 transition-all" style={{ fontFamily: 'Outfit' }}>
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting...</> : <><ShoppingCart className="w-4 h-4" /> Accept &amp; Pay Deposit</>}
            </button>
          )}
          <Link to={inStoreMode ? "/admin" : "/"} className="block text-sm text-[#A0B8C7] hover:text-white mt-4 transition">{inStoreMode ? 'Back to Admin' : 'Back to Home'}</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1C29]" data-testid="quote-builder-page">
      <div className="bg-[#0F2A3D] border-b border-[#1E4159]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#A0B8C7] hover:text-white text-sm transition" style={{ fontFamily: 'Manrope' }}><ArrowLeft className="w-4 h-4" /> Back</Link>
          <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }}>{editingQuoteId ? 'Edit Quote' : 'Get Your Quote'}</h1>
          <div className="w-16" />
        </div>
      </div>
      <div className="bg-[#0F2A3D]/50 border-b border-[#1E4159]/50">
        <div className="max-w-md mx-auto px-6 py-4"><ProgressBar current={step} /></div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm flex items-center gap-2" data-testid="quote-error"><AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}</div>}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div key={step} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.25, ease: 'easeInOut' }}>
                {step === 1 && <StepInfo data={customerInfo} onChange={setCustomerInfo} />}
                {step === 2 && <StepSource value={garmentSource} onChange={setGarmentSource} />}
                {step === 3 && <StepItems items={items} onItemsChange={setItems} products={products} pricingData={pricingData} printingPricing={printingPricing} sizeGuide={sizeGuide} productOptions={productOptions} garmentSource={garmentSource} />}
                {step === 4 && <StepReview customerInfo={customerInfo} garmentSource={garmentSource} items={items} sizeGuide={sizeGuide} pricingData={pricingData} printingPricing={printingPricing} products={products} rushOption={rushOption} onRushChange={setRushOption} shippingOption={shippingOption} onShippingChange={setShippingOption} byoAcknowledged={byoAcknowledged} onByoAcknowledgedChange={setByoAcknowledged} />}
              </motion.div>
            </AnimatePresence>
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#1E4159]">
              {step > 1 ? (
                <button onClick={goBack} data-testid="back-btn" className="flex items-center gap-2 px-6 py-3 rounded-xl border border-[#1E4159] text-[#A0B8C7] hover:border-[#4A90A4] hover:text-white font-medium text-sm transition-all" style={{ fontFamily: 'Manrope' }}>
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              ) : <div />}
              {step < 4 ? (
                <button onClick={step === 1 ? handleContinueFromStep1 : goNext} disabled={!canProceed} data-testid="next-step-button" className="flex items-center gap-2 px-8 py-3 rounded-xl bg-[#F5A623] text-[#0A1C29] font-bold text-sm hover:bg-[#D88F1B] disabled:opacity-30 disabled:cursor-not-allowed transition-all" style={{ fontFamily: 'Outfit' }}>
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={handleSubmit} disabled={submitting || !canProceed} data-testid="submit-quote-btn" className="flex items-center gap-2 px-8 py-3 rounded-xl bg-[#F5A623] text-[#0A1C29] font-bold text-sm hover:bg-[#D88F1B] disabled:opacity-40 disabled:cursor-not-allowed transition-all" style={{ fontFamily: 'Outfit' }}>
                  {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</> : <><Check className="w-4 h-4" /> Submit Quote Request</>}
                </button>
              )}
            </div>
          </div>
          <div className="lg:col-span-1 hidden lg:block">
            <div className="sticky top-6">
              <Sidebar customerInfo={customerInfo} garmentSource={garmentSource} items={items} pricingData={pricingData} products={products} total={total} sizeGuide={sizeGuide} />
            </div>
          </div>
        </div>
      </div>
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-[#0F2A3D] border-t border-[#1E4159] px-4 py-3 flex items-center justify-between z-50">
        <div>
          <p className="text-[10px] text-[#A0B8C7]/60 uppercase tracking-wider" style={{ fontFamily: 'Outfit' }}>Estimated Total</p>
          <p className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit' }} data-testid="mobile-total">${total.total.toFixed(2)}</p>
        </div>
        <p className="text-xs text-[#F5A623] font-semibold" style={{ fontFamily: 'Manrope' }}>Deposit: ${total.deposit.toFixed(2)}</p>
      </div>
    </div>
  );
};

export default QuoteBuilderPage;
